import { ChangeEvent } from 'react';
import classNames from 'classnames';
import { TrafficInfoResponse } from '../traffic-info-types';
import styles from './traffic-info-filter.module.scss';
import { useTrafficInfoContext } from '../traffic-info-context';
import { buildLineComboBoxList } from '../traffic-info-utils';
import { DropdownItemProps } from '../../dropdown-widget';
import { TextInputComponent } from '../../../components/controls/text-input/text-input';
import { ComboBoxComponent } from '../../../components/controls/combo-box/combo-box';
import { MultiSelectDropdownComponent } from '../../../components/controls/multi-select-dropdown/multi-select-dropdown';

export interface TrafficInfoFilterProps {
  data: TrafficInfoResponse;
}

export const TrafficInfoFilter = ({ data }: TrafficInfoFilterProps): JSX.Element => {
  const trafficInfoContext = useTrafficInfoContext();
  const lineComboBoxList = buildLineComboBoxList(data.infos, trafficInfoContext.typeFilters);
  const activeTypeFilters = trafficInfoContext.typeFilters
    .filter((filter) => filter.active)
    .map((filter) => filter.type);

  const onLineNumberFilterChange = (e: DropdownItemProps): void => {
    trafficInfoContext.setLineNumberFilter(e.value);
  };

  const onDateFilterChange = (value: string | ChangeEvent<HTMLInputElement>): void => {
    // Desktop (MUI DatePicker) passes a DD.MM.YYYY string; mobile native input passes a ChangeEvent with YYYY-MM-DD.
    if (typeof value !== 'string') {
      trafficInfoContext.setDateFilter(value.target.value || undefined);
      return;
    }

    if (!/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
      trafficInfoContext.setDateFilter(undefined);
      return;
    }

    trafficInfoContext.setDateFilter(value.split('.').reverse().join('-'));
  };

  const onTypeFilterChange = (selectedTypes: string[]): void => {
    trafficInfoContext.setTypeFilters(
      trafficInfoContext.typeFilters.map((filter) => ({
        ...filter,
        active: selectedTypes.includes(filter.type),
      }))
    );
  };

  return (
    <div className={styles.TrafficInfoFilter}>
      <div className={styles.TrafficInfoFilterHeadline}>
        Verkehrsmeldungen, Baustellen und Aufzugstörungen eingrenzen
      </div>

      <div className={styles.TrafficInfoFilterDropdowns}>
        <MultiSelectDropdownComponent
          label="Verkehrsmittel"
          items={trafficInfoContext.typeFilters.map((filter) => ({
            value: filter.type,
            label: filter.label,
            adornment: <span className={classNames(styles.TrafficInfoFilterItemTag, filter.className)} />,
          }))}
          value={activeTypeFilters}
          onChange={onTypeFilterChange}
        />
        <ComboBoxComponent
          label="Linie"
          options={lineComboBoxList}
          showClearButton
          onValueChange={onLineNumberFilterChange}
        />
        <TextInputComponent
          label="Datum"
          type="date"
          value={trafficInfoContext.dateFilter ?? ''}
          onChange={onDateFilterChange}
        />
      </div>
    </div>
  );
};
